<template>
  <!-- Company Details -->
  <div class="flex flex-col xl:flex-row w-full bg-transparent xl:space-x-4">

    <div class="xl:flex xl:flex-col max-h-full xl:overflow-y-auto p-0 mt-4 xl:w-7/12 bg-transparent">

      <section class="mb-4 xl:mr-3">
        <div class="w-full bg-white rounded-lg border divide-y divide-gray-200 shadow">
          <div class="px-4 py-5 sm:p-6">


            <!-- Header -->
            <div class="flex flex-col items-center space-x-3 sm:flex-row sm:items-start sm:mb-2">
              <!-- Logo -->
              <div class="flex-shrink-0">
                <img class="w-12 h-12 rounded sm:h-20 sm:w-20" :src="company.logo" alt="{{ company.name }}"/>
              </div>
              <!-- Title -->
              <div class="min-w-0 text-base text-gray-900">
                <h1 class="text-xl font-bold">{{ company.name }}</h1>
                <p class="mt-2 space-y-4 line-clamp-3" v-html="companyDescription"></p>
              </div>
              <div class="flex flex-1 justify-end my-4 sm:my-0" v-if="company._metadata.score >= 0.01">
                <!-- self-center  -->
                <StarIcon v-for="rating in [0, 1, 2, 3, 4]" :key="rating"
                          :class="[company._metadata.score * 100 > rating ? 'text-yellow-400' : 'text-gray-200', 'h-5 w-5 flex-shrink-0']"
                          aria-hidden="true"/>
              </div>
            </div>


            <div class="flex flex-col justify-center space-y-6 sm:flex-row sm:justify-between sm:space-y-0">
              <div class="flex-col text-sm text-gray-900 sm:flex sm:pl-20 sm:ml-3">
                <p v-if="websiteUrl !== null" class="flex items-center py-1">
                  <GlobeIcon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-500" aria-hidden="true"/>
                  <a class="inner_link" target="_blank" rel="noopener noreferrer"
                     :href="this.companyDetails.websiteUrl">
                    {{ websiteUrl }}
                  </a>
                </p>
                <p v-if="companyLocations !== undefined" class="flex items-center py-1">
                  <LocationMarkerIcon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-500" aria-hidden="true"/>
                  <a v-for="(location, locationIdx) in companyLocations" :key="`${location}-${locationIdx}`"
                     class="inner_link" rel="noopener nofollow noreferrer" target="_blank"
                     :href="'https://maps.google.com/?q=' + encodeURIComponent(location.replace('(HQ)', '').trim())">
                    {{ locationIdx !== 0 ? ', ' : '' }}{{ location }}
                  </a>
                </p>
                <p v-if="isLoadedCompanyDetails && companyDetails.nbEmployees !== null && companyDetails.nbEmployees > 0"
                   class="flex items-center py-1">
                  <UserGroupIcon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-500" aria-hidden="true"/>
                  {{ companyDetails.nbEmployees }} employee{{ companyDetails.nbEmployees > 1 ? 's' : '' }}
                </p>
                <p v-if="isLoadedCompanyDetails && companyDetails.launchDate !== null" class="flex items-center py-1">
                  <CalendarIcon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-500" aria-hidden="true"/>
                  Launch date: {{ (new Date(companyDetails.launchDate)).getFullYear() }}
                </p>
                <p v-if="isLoadedCompanyDetails && companyDetails.stage !== null" class="flex items-center py-1">
                  <svg fill="currentColor" aria-hidden="true" class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-500"
                       viewBox="0 0 567 567" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="m538 255h-23c-13-106-97-190-203-203v-23c0-16-13-29-29-29-16 0-29 13-29 29v23c-106 13-190 97-203 203h-23c-16 1e-3 -29 13-29 29 0 16 13 29 29 29h23c13 106 97 190 203 203v23c0 16 13 29 29 29 16 0 29-13 29-29v-23c106-13 190-97 203-203h23c16 0 29-13 29-29-2e-3 -16-13-29-29-29zm-81 0-73-6e-3c-10-35-37-62-72-72v-73c75 12 133 71 146 146zm-203-146v73c-35 10-62 37-72 72l-73-0.01c12-75 71-133 146-146zm-146 203 73 8e-3c10 35 37 62 72 72v73c-75-12-133-71-146-146zm203 146v-73c35-10 62-37 72-72l73 8e-3c-12 75-71 133-146 146z"/>
                  </svg>
                  Stage: {{ companyDetails.stage }}
                </p>
              </div>


              <div class="flex space-x-3 sm:flex-col sm:space-y-3 sm:space-x-0">

                <!-- Facebook -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.facebookUrl === 'string'"
                   :href="companyDetails.facebookUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-blue-600 fill-current"
                       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>

                <!-- Twitter -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.twitterUrl === 'string'"
                   :href="companyDetails.twitterUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-blue-300 fill-current"
                       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>

                <!-- Whatsapp -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.whatsappUrl === 'string'"
                   :href="companyDetails.whatsappUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-green-400 fill-current"
                       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
                  </svg>
                </a>

                <!-- Linkedin -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.linkedInUrl === 'string'"
                   :href="companyDetails.linkedInUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-blue-500 fill-current"
                       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"></path>
                  </svg>
                </a>

                <!-- telegram -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.telegramUrl === 'string'"
                   :href="companyDetails.telegramUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-blue-400 text-gray-400 fill-current"
                       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M248,8C111.033,8,0,119.033,0,256S111.033,504,248,504,496,392.967,496,256,384.967,8,248,8ZM362.952,176.66c-3.732,39.215-19.881,134.378-28.1,178.3-3.476,18.584-10.322,24.816-16.948,25.425-14.4,1.326-25.338-9.517-39.287-18.661-21.827-14.308-34.158-23.215-55.346-37.177-24.485-16.135-8.612-25,5.342-39.5,3.652-3.793,67.107-61.51,68.335-66.746.153-.655.3-3.1-1.154-4.384s-3.59-.849-5.135-.5q-3.283.746-104.608,69.142-14.845,10.194-26.894,9.934c-8.855-.191-25.888-5.006-38.551-9.123-15.531-5.048-27.875-7.717-26.8-16.291q.84-6.7,18.45-13.7,108.446-47.248,144.628-62.3c68.872-28.647,83.183-33.623,92.511-33.789,2.052-.034,6.639.474,9.61,2.885a10.452,10.452,0,0,1,3.53,6.716A43.765,43.765,0,0,1,362.952,176.66Z"></path>
                  </svg>
                </a>

                <!-- Pinterest -->
                <a v-if="isLoadedCompanyDetails && typeof companyDetails.pinterestUrl === 'string'"
                   :href="companyDetails.pinterestUrl" class="text-gray-400 hover:text-gray-500" target="_blank"
                   rel="noreferrer noopener nofollow">
                  <svg fill="currentColor" aria-hidden="true" class="w-6 h-6 text-pink-600 fill-current"
                       viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
                  </svg>
                </a>

              </div>

            </div>


          </div>
          <div class="p-4 sm:px-6">


            <div v-if="company.tags.length > 0">
              <div class="flex">
                <TagIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true"/>
                <div class="pl-1 mt-0 ml-1 border-l sm:ml-2 flex-shrink-1 sm:pl-2">
                  <div class="flex flex-wrap items-center -m-1">
                                      <span v-for="tag in company.tags"
                                            :key="company.id+'-'+tag.value"
                                            :class="{ 'bg-primary-100': tagsReferential[tag.value].enable }"
                                            class="inline-flex items-center pl-2 m-1 text-sm font-medium text-gray-900 bg-white rounded-full border border-primary-200"
                                      >
                                        <span class="lowercase">{{ tag.label }}</span>
                                        <button type="button" v-if="! tagsReferential[tag.value].enable"
                                                @click="$emit('use-tag', {'tag': tag.value, 'use': true})"
                                                class="inline-flex flex-shrink-0 p-1 ml-1 w-5 h-5 text-gray-400 rounded-full hover:bg-primary-200 hover:text-gray-500"
                                                title="Use this tag in your search">
                                          <svg class="w-3 h-3" stroke="currentColor" fill="none" viewBox="0 0 20 20">
                                              <path stroke-width="1.5" fill-rule="evenodd"
                                                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                                    clip-rule="evenodd"/>
                                          </svg>
                                        </button>
                                        <button type="button" v-if="tagsReferential[tag.value].enable"
                                                @click="$emit('use-tag', {'tag': tag.value, 'use': false})"
                                                class="inline-flex flex-shrink-0 justify-center items-center p-1 ml-1 w-5 h-5 text-gray-400 rounded-full hover:bg-primary-200 hover:text-gray-500"
                                                title="Click to remove this tag from your search">
                                          <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                            <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                          </svg>
                                        </button>
                                      </span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </section>

      <section v-if="hasCompanyValueProposition" class="mb-4 xl:mr-3">
        <div class="overflow-hidden text-gray-900 bg-white rounded-lg border divide-y divide-gray-200 shadow">
          <div class="px-4 py-5 uppercase sm:px-6"><h2>Value Proposition</h2></div>
          <div class="px-4 py-5 sm:p-6">
            <p class="text-justify" v-html="companyDetails.valueProposition"></p>
          </div>
        </div>
      </section>

      <section v-if="hasCompanyFunding || hasCompanyInvestors" class=" mb-4 xl:mr-3 text-left">
        <div class="overflow-hidden text-gray-900 bg-white rounded-lg border divide-y divide-gray-200 shadow">
          <div class="overflow-hidden bg-white shadow sm:rounded-lg">
            <div class="px-4 py-5 sm:p-6">

              <!-- Tabs header -->
              <div>
                <div class="sm:hidden">
                  <select v-model="activeFinancialTabs"
                          class="block py-2 pr-10 pl-3 w-full text-base rounded-md border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    <option v-if="hasCompanyFunding" :selected="activeFinancialTabs === '1'" value="1">Financial
                      Rounds
                    </option>
                    <option v-if="hasCompanyInvestors" :selected="activeFinancialTabs === '2'" value="2">Investors
                    </option>
                  </select>
                </div>
                <div class="hidden sm:block">
                  <div class="border-b border-gray-200">
                    <nav class="flex -mb-px space-x-8" aria-label="Tabs">
                      <button v-if="hasCompanyFunding"
                              @click="activeFinancialTabs = '1'"
                              :class="[activeFinancialTabs === '1' ? 'border-primary-500 text-secondary-500' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm uppercase']"
                              :aria-current="activeFinancialTabs === '1' ? 'page' : undefined"
                      >
                        Financial Rounds
                      </button>
                      <button v-if="hasCompanyInvestors"
                              @click="activeFinancialTabs = '2'"
                              :class="[activeFinancialTabs === '2' ? 'border-primary-500 text-secondary-500' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm uppercase']"
                              :aria-current="activeFinancialTabs === '2' ? 'page' : undefined"
                      >
                        Investors
                      </button>
                    </nav>
                  </div>
                </div>
              </div>

              <!-- Tabs Content -->
              <div v-if="hasCompanyFunding"
                   v-show="activeFinancialTabs === '1'"
                   class="overflow-hidden mt-4 bg-white sm:mt-0">
                <div class="pb-2">

                  <div class="flex flex-col">
                    <div class="overflow-x-auto -my-2 sm:-mx-6 lg:-mx-8">
                      <div class="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
                        <div class="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                          <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr class="text-xs text-gray-900 uppercase">
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Date
                              </th>
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Investor
                              </th>
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Amount
                              </th>
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Round
                              </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(round, roundIdx) in companyDetails.funding" :key="'round-'+roundIdx"
                                :class="roundIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                class="text-sm font-medium text-gray-900 whitespace-nowrap">
                              <td class="px-3 py-2">
                                {{ round.date }}
                              </td>
                              <td class="px-3 py-2">
                                {{ round.investors }}
                              </td>
                              <td class="px-3 py-2">
                                {{ round.amount }}
                              </td>
                              <td class="px-3 py-2">
                                {{ round.round }}
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>


              <div v-if="hasCompanyInvestors"
                   v-show="activeFinancialTabs === '2'"
                   class="overflow-hidden mt-4 bg-white sm:mt-0">
                <div class="pb-2">

                  <div class="flex flex-col">
                    <div class="overflow-x-auto -my-2 sm:-mx-6 lg:-mx-8">
                      <div class="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
                        <div class="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                          <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr class="text-xs text-gray-900 uppercase">
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Investor
                              </th>
                              <th scope="col" class="px-3 py-3 font-medium tracking-wider">
                                Amount
                              </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(investor, investorIdx) in companyDetails.investors"
                                :key="'investor-'+investorIdx"
                                :class="investorIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                class="text-sm font-medium text-gray-900 whitespace-nowrap">
                              <td class="px-3 py-2">
                                {{ investor.name }}
                              </td>
                              <td class="px-3 py-2">
                                {{ investor.subtitle }}
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </section>



      <section class="mb-4 xl:mr-3">
        <div class="overflow-hidden text-gray-900 bg-white rounded-lg border divide-y divide-gray-200 shadow">
          <div class="px-4 py-5 uppercase sm:px-6"><h2>Attachments ({{ attachmentCount }})</h2></div>
          <div class="px-4 py-5 sm:p-6">
            <company-attachment
                :company="company"
                @attachments:count="newValue => attachmentCount = newValue"
                @attachments:add="attachment => $emit('attachments:add', attachment)"
                @attachments:remove="attachment => $emit('attachments:remove', attachment)"
            ></company-attachment>
          </div>
        </div>
      </section>


      <section class=" mb-0.5 xl:mr-3 text-left">
        <div class="overflow-hidden text-gray-900 bg-white rounded-lg border divide-y divide-gray-200 shadow">
          <div class="px-4 py-5 uppercase sm:px-6">
            <h2>Recent News</h2>
          </div>
          <div class="px-4 py-1 sm:px-6 sm:py-2">


            <ul  v-if="hasCompanyRelatedNews" role="list" class="divide-y divide-gray-200 ">
              <li v-for="(relatedNew, idx) in relatedNews" :key="idx"
                  class="relative py-4 bg-white hover:bg-gray-50 focus-within:ring-1 focus-within:ring-inset focus-within:ring-primary">
                <div class="flex justify-between space-x-3">
                  <div class="flex-1 min-w-0">
                    <a :href="relatedNew.url" class="block focus:outline-none" rel="nofollow noopener noreferrer" target="_blank">
                      <span class="absolute inset-0" aria-hidden="true"/>
                      <p class="text-sm font-semibold text-gray-700 truncate">{{ relatedNew.title }}</p>
                      <p class="text-xs font-semibold text-gray-500 truncate">Via {{ relatedNew.source.name }}</p>
                    </a>
                  </div>
                  <time :datetime="relatedNew.publishedAt" class="flex-shrink-0 text-sm text-gray-500 italic whitespace-nowrap">
                    {{ timeAgo(new Date(relatedNew.publishedAt)) }}
                  </time>
                </div>
                <div v-if="relatedNew.description !== undefined && relatedNew.description !== null" class="mt-1">
                  <p class="text-sm text-gray-600 line-clamp-2">
                    {{ relatedNew.description }}
                  </p>
                </div>
              </li>
            </ul>
            <div v-else class="py-8">
                <p class="text-sm text-gray-600 line-clamp-2">
                  No data available.
                </p>
            </div>

          </div>
        </div>

      </section>
    </div>



    <div class="xl:flex xl:flex-col max-h-full xl:overflow-y-auto p-0 mt-4 xl:w-5/12 bg-transparent">
      <company-comments class="mb-0.5 xl:mr-3 xl:mx-3 bg-white" :company="company" :member-profile="memberProfile"></company-comments>
    </div>
  </div>
  <!-- End Company Details -->
</template>

<script>
import {CalendarIcon, ExclamationIcon, GlobeIcon, UserGroupIcon,} from '@heroicons/vue/outline'
import {LocationMarkerIcon, StarIcon, TagIcon,} from '@heroicons/vue/solid'
import {getMinifiedUrl} from "@/Utilities/Url";
import _isEmpty from "lodash/isEmpty"
import _get from 'lodash/get'
import CompanyComments from "@/components/ContentCard/CompanyComments";
import CompanyAttachment from "@/components/ContentCard/CompanyAttachment";
import { format, register } from 'timeago.js';
import jalocaleFunc from 'timeago.js/lib/lang/ja'

export default {
  name: "CompanyOverview",
  components: {
    CompanyAttachment,
    CompanyComments,
    ExclamationIcon,
    TagIcon,
    LocationMarkerIcon,
    StarIcon,
    GlobeIcon,
    UserGroupIcon,
    CalendarIcon,
  },
  props: ['company', 'companyDetails', 'relatedNews', 'tagsReferential', 'memberProfile'],
  emits: ['use-tag', 'attachments:add', 'attachments:remove'],
  data() {
    return {
      localeToUse: 'en',
      activeFinancialTabs: "1",
      attachmentCount: 0,
    }
  },
  created() {
    if(this.memberProfile.locale === 'ja_JP') {
      register('ja', jalocaleFunc);
      this.localeToUse = 'ja';
    }
  },
  methods: {
    timeAgo (date) {
      return format(date, this.localeToUse);
    },
  },

  watch: {
    activeFinancialTabs(value) {
      if (typeof value === "string") {
        return parseInt(value);
      }
    }
  },

  computed: {
    isLoadedCompanyDetails() {
      return typeof this.companyDetails === 'object' && typeof this.companyDetails.slug === 'string';
    },
    websiteUrl() {
      if (typeof _get(this.companyDetails, 'websiteUrl', null) !== 'string') {
        return null;
      }

      return getMinifiedUrl(this.companyDetails.websiteUrl);
    },
    companyLocations() {
      // if(this.companyDetailsLoaded && this.companyDetails.locations !== undefined && Array.isArray(this.companyDetails.locations)) {
      //   return this.companyDetails.locations.map(location => location.address)
      // }

      if (typeof _get(this.companyDetails, 'address', null) === 'string') {
        return this.companyDetails.address.split(',').map(s => s.trim());
      }

      return undefined;
    },
    companyDescription() {
      return _get(this.company, 'excerpt', '').length ? this.company.excerpt :
          _get(this.company, 'description_short', '').length ? this.company.description_short :
              _get(this.company, 'description', '').length ? this.company.description : 'No description available.';
    },
    hasCompanyValueProposition() {
      return this.isLoadedCompanyDetails
          && !_isEmpty(this.companyDetails.valueProposition);
    },
    hasCompanyFunding() {
      return this.isLoadedCompanyDetails
          && !_isEmpty(this.companyDetails.funding)
          && Array.isArray(this.companyDetails.funding);
    },
    hasCompanyInvestors() {
      return this.isLoadedCompanyDetails
          && !_isEmpty(this.companyDetails.investors)
          && Array.isArray(this.companyDetails.investors);
    },
    hasCompanyRelatedNews() {
      return this.relatedNews !== undefined
          && !_isEmpty(this.relatedNews)
          && Array.isArray(this.relatedNews);
    },
  }
}
</script>